import {
  ANALYTICS_SEQUENCE_KEY,
  ANALYTICS_SESSION_INACTIVITY_MS,
  ANALYTICS_SESSION_STORAGE_KEY,
  ANALYTICS_VISITOR_STORAGE_KEY,
} from './config';
import { isAnalyticsConsentGranted } from '../utils/cookieConsent';

export type AnalyticsSessionState = {
  sessionId: string;
  startedAt: string;
  lastActivityAt: string;
  landingPageType?: string;
};

const newId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export function getOrCreateVisitorId(): string | null {
  if (!isAnalyticsConsentGranted()) return null;
  try {
    const existing = window.localStorage.getItem(ANALYTICS_VISITOR_STORAGE_KEY);
    if (existing) return existing;
    const id = newId();
    window.localStorage.setItem(ANALYTICS_VISITOR_STORAGE_KEY, id);
    return id;
  } catch {
    return null;
  }
}

export function clearVisitorId(): void {
  try {
    window.localStorage.removeItem(ANALYTICS_VISITOR_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function loadSessionState(): AnalyticsSessionState | null {
  try {
    const raw = window.sessionStorage.getItem(ANALYTICS_SESSION_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AnalyticsSessionState;
  } catch {
    return null;
  }
}

export function saveSessionState(state: AnalyticsSessionState): void {
  try {
    window.sessionStorage.setItem(ANALYTICS_SESSION_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export function resetAnalyticsSession(landingPageType?: string): AnalyticsSessionState {
  const now = new Date().toISOString();
  const state: AnalyticsSessionState = {
    sessionId: newId(),
    startedAt: now,
    lastActivityAt: now,
    landingPageType,
  };
  saveSessionState(state);
  try {
    window.sessionStorage.setItem(ANALYTICS_SEQUENCE_KEY, '0');
  } catch {
    // ignore
  }
  return state;
}

export function touchSession(landingPageType?: string): AnalyticsSessionState {
  const now = Date.now();
  const existing = loadSessionState();
  if (
    existing &&
    now - new Date(existing.lastActivityAt).getTime() < ANALYTICS_SESSION_INACTIVITY_MS
  ) {
    const updated = { ...existing, lastActivityAt: new Date().toISOString() };
    saveSessionState(updated);
    return updated;
  }
  return resetAnalyticsSession(landingPageType);
}

export function nextSequenceNumber(): number {
  try {
    const raw = window.sessionStorage.getItem(ANALYTICS_SEQUENCE_KEY);
    const current = raw ? parseInt(raw, 10) : 0;
    const next = Number.isFinite(current) ? current + 1 : 1;
    window.sessionStorage.setItem(ANALYTICS_SEQUENCE_KEY, String(next));
    return next;
  } catch {
    return 1;
  }
}
